import React, { useState, useEffect } from 'react';
import { useUserContext } from '@nexus/fff-utilities';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { SubscriptionUpdatePaymentModal } from '@nexus/fff-payments';
import { trackEvent, trackPageLoad } from '@nexus/fff-analytics';

import Layout from '../../components/Layout';
import MainBanner from './MainBanner';
import OptionButtons from '../TrialUpgrade/OptionButtons';
import PerksList from './PerksList';
import {
  annualCoupons, couponScreenKey, Plans, PlansLiteral,
} from '../constants';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import useShowErrorOrRedirectThankYouPage from '../../hooks/useShowPayErrorOrThankYouPage';
import { searchToObject, setDefaultPlan } from '../../helpers';
import Coupons from './Coupons';
import useCouponContent from '../../hooks/useCouponContent';
import useCouponSelector from '../../hooks/useCouponSelector';
import './styles.scss';

export const Reactivate = ({
  flags, ldClient, location: { pathname, search }, history,
}) => {
  let { coupon_code: couponCode } = searchToObject(search);
  couponCode = couponCode ? couponCode.toLowerCase() : '';
  const { userInfo } = useUserContext();
  const [selected, setSelected] = useState(
    setDefaultPlan(annualCoupons.includes(couponCode), userInfo.user.subscriptionState, userInfo.user.subscriptionType),
  );
  const myAccountPath = pathname && pathname.indexOf('/my-account-v3') === -1 ? '' : '/my-account-v3';
  const thanksPage = `${myAccountPath}/reactivate-thanks`;
  const { displayAdditionalPerks } = flags || {};
  const subscriptionState = userInfo && userInfo.user ? userInfo.user.subscriptionState : '';
  const loggedIn = userInfo && userInfo.loggedIn;
  const { getCouponContentKey, handleCouponChange } = useCouponContent(couponScreenKey.REACTIVATE_EXPIRED_SUBSCRIPTION);
  useLaunchDarkly(ldClient, userInfo);
  const {
    handleApplyCoupon,
    handleRemoveCoupon,
    couponsApplied,
    couponError,
    discount,
  } = useCouponSelector(handleCouponChange);
  const {
    showPayment,
    errorPaymentMessage,
    changeReactivateSubscription,
    onClose,
    isLoading,
  } = useShowErrorOrRedirectThankYouPage(history, {
    pathname: thanksPage,
    search: '?reactivate=success',
  }, '', couponsApplied);

  useEffect(() => {
    if (loggedIn && subscriptionState === 'EXPIRED') {
      trackPageLoad({
        properties: { coupon_code: couponCode },
      }).then();
    }
  }, []);

  if (loggedIn && subscriptionState === 'CANCELED') {
    history.replace(`${myAccountPath}/reactivate-today${search}`);
    return null;
  }
  if (loggedIn && subscriptionState !== 'EXPIRED') {
    window.location.replace(process.env.REACT_APP_SITE_URL);
    return null;
  }

  const handleChange = (membership) => {
    setSelected(membership);
    handleRemoveCoupon();
  };

  const handleClick = () => {
    trackEvent({
      ctaName: 'reactivate-my-membership',
      properties: {
        plan_selected: Plans.ANNUAL === selected ? PlansLiteral.ANNUAL : PlansLiteral.SEASONAL,
        CTA: 'reactivate-my-membership',
      },
    });
    changeReactivateSubscription(selected);
  };

  return (
    <Layout isLoading={isLoading} pageTitle="Expired Reactivation" redirectIfNoAuth>
      <MainBanner getCouponContentKey={getCouponContentKey} planSelected={selected} />
      <div className="buttons-container">
        <OptionButtons
          handleChange={handleChange}
          selected={selected}
          getCouponContentKey={getCouponContentKey}
        />
      </div>
      <div className="reactivate-list-container">
        <PerksList
          membership={selected}
          showAdditionalPerks={displayAdditionalPerks}
          getCouponContentKey={getCouponContentKey}
        />
      </div>
      <div className="body-container">
        <Coupons
          membership={selected}
          couponCode={couponCode}
          couponsApplied={couponsApplied}
          couponError={couponError}
          handleApplyCoupon={handleApplyCoupon}
          handleRemoveCoupon={handleRemoveCoupon}
          getCouponContentKey={getCouponContentKey}
          handleCouponChange={handleCouponChange}
          couponDiscount={discount}
        />
        {/* eslint-disable-next-line react/no-danger */}
        <div className="reactivate-notice" dangerouslySetInnerHTML={{ __html: getCouponContentKey('notice', '') }} />
        <div className="reactivate-action text-center">
          <button
            type="button"
            className="btn btn-lg btn-cta"
            onClick={handleClick}
            disabled={isLoading}
          >
            {getCouponContentKey('buttonText', 'REACTIVATE ME')}
          </button>
        </div>
        <SubscriptionUpdatePaymentModal
          onClose={onClose}
          subtitle={errorPaymentMessage}
          isOpen={showPayment}
          onSuccess={handleClick}
          hasError={!!errorPaymentMessage}
        />
      </div>
    </Layout>
  );
};

export default withLDConsumer()(Reactivate);
